import z from "zod"

const OrderAddressFieldsSchema = z.object({
  firstName: z.string().nullish(),
  lastName: z.string().nullish(),
  name: z.string().nullish(),
  phone: z.string().nullish(),
  email: z.string().nullish(),
  address1: z.string().nullish(),
  address2: z.string().nullish(),
  address3: z.string().nullish(),
  address4: z.string().nullish(),
  line1: z.string().nullish(),
  city: z.string().nullish(),
  zip: z.string().nullish(),
  postalCode: z.string().nullish(),
  state: z.string().nullish(),
  province: z.string().nullish(),
  country: z.string().nullish(),
  countryCode: z.string().nullish(),
})

export const OrderAddressSchema = z.preprocess(normalizeLegacyPostal, OrderAddressFieldsSchema)

export const OrderPudoAddressSchema = z.preprocess(
  normalizeLegacyPostal,
  OrderAddressFieldsSchema.extend({
    code: z.string().nullish(),
    carrierService: z.string().nullish(),
    provinceCode: z.string().nullish(),
    latitude: z.number().nullish(),
    longitude: z.number().nullish(),
    type: z.string().nullish(),
    schedule: z.record(z.string(), z.unknown()).nullish(),
  }),
)

export const OrderMetafieldSchema = z.object({
  key: z.string(),
  value: z.string(),
  description: z.string(),
  createdAt: z.string().nullish(),
  updatedAt: z.string().nullish(),
})

export const OrderLineItemSchema = z.object({
  id: z.string().nullish(),
  shopifyLineItemId: z.string().nullish(),
  shopifyProductId: z.string().nullish(),
  shopifyVariantId: z.string().nullish(),
  name: z.string().nullish(),
  productId: z.string().nullish(),
  variantId: z.string().nullish(),
  variantTitle: z.string().nullish(),
  selectedOptions: z.array(z.object({ name: z.string(), value: z.string() })).optional(),
  sku: z.string().nullish(),
  shopifySku: z.string().nullish(),
  logisticsSku: z.string().nullish(),
  logisticsProductId: z.string().nullish(),
  logisticsQuantity: z.number().nullish(),
  quantity: z.number().nullish(),
  price: z.string().nullish(),
  discountedUnitPrice: z.object({ amount: z.string(), currency: z.string() }).nullish(),
  totalPrice: z.number().nullish(),
  batchName: z.string().nullish(),
  hasDiscounts: z.boolean().optional(),
  properties: z
    .array(z.object({ key: z.string(), value: z.string(), description: z.string() }))
    .optional(),
  metafields: z.array(OrderMetafieldSchema).optional(),
})

export const OrderShipmentLineItemSchema = z.object({
  lineItemId: z.string().nullish(),
  quantity: z.number().nullish(),
  remainingQuantity: z.number().nullish(),
})

export const OrderFulfillmentHoldSchema = z.object({
  reason: z.string().nullish(),
  reasonNotes: z.string().nullish(),
})

export const OrderTrackingEventSchema = z.object({
  source: z.enum(["shopify", "logistics"]).optional(),
  code: z.string().nullish(),
  status: z.string().nullish(),
  message: z.string().nullish(),
  location: z.string().nullish(),
  shippingNumber: z.string().nullish(),
  isReturn: z.boolean().nullish(),
  isDeliveryAttempt: z.boolean().nullish(),
  refuseReason: z.string().nullish(),
  happenedAt: z.string().nullish(),
  estimatedDeliveryAt: z.string().nullish(),
  city: z.string().nullish(),
  province: z.string().nullish(),
  country: z.string().nullish(),
  zip: z.string().nullish(),
})

export const OrderTrackingSchema = z.object({
  company: z.string().nullish(),
  number: z.string().nullish(),
  carrierReference: z.string().nullish(),
  url: z.string().nullish(),
  status: z.string().nullish(),
  displayStatus: z.string().nullish(),
  refused: z.boolean().nullish(),
  hasReturn: z.boolean().nullish(),
  deliveryNeedsReview: z.boolean().optional(),
  hasReadings: z.boolean().nullish(),
  firstReadingAt: z.string().nullish(),
  eventsStatus: z.enum(["not_queried", "available", "error"]).optional(),
  deliveredAt: z.string().nullish(),
  inTransitAt: z.string().nullish(),
  estimatedDeliveryAt: z.string().nullish(),
  events: z.array(OrderTrackingEventSchema).optional(),
})

export const OrderDeliveryMethodSchema = z.object({
  methodType: z.string().nullish(),
  presentedName: z.string().nullish(),
  serviceCode: z.string().nullish(),
  minDeliveryDateTime: z.string().nullish(),
  maxDeliveryDateTime: z.string().nullish(),
})

export const OrderShipmentSchema = z.object({
  id: z.string().nullish(),
  status: z.string().nullish(),
  statusMeaning: z.string().nullish(),
  packedAt: z.string().nullish(),
  requestStatus: z.string().nullish(),
  fulfillAt: z.string().nullish(),
  fulfillBy: z.string().nullish(),
  estimatedDeliveryAt: z.string().nullish(),
  deliveryMethod: OrderDeliveryMethodSchema.nullish(),
  destination: OrderAddressSchema.nullish(),
  assignedLocation: OrderAddressSchema.nullish(),
  fulfillmentHolds: z.array(OrderFulfillmentHoldSchema).optional(),
  tracking: z.array(OrderTrackingSchema).default([]),
  lineItems: z.array(OrderShipmentLineItemSchema).default([]),
  shippingLineId: z.string().nullish(),
  shippingLineMatchConfidence: z.enum(["exact", "single_order_line", "none"]).optional(),
})

export const OrderShippingLineSchema = z.object({
  id: z.string().nullish(),
  title: z.string().nullish(),
  code: z.string().nullish(),
  carrierIdentifier: z.string().nullish(),
  deliveryCategory: z.string().nullish(),
  price: z
    .object({
      amount: z.string(),
      currency: z.string(),
    })
    .nullish(),
})

export const OrderReturnLineItemSchema = z.object({
  id: z.string(),
  quantity: z.number().nullish(),
  customerNote: z.string().nullish(),
  returnReason: z.string().nullish(),
  returnReasonNote: z.string().nullish(),
  processableQuantity: z.number().nullish(),
  processedQuantity: z.number().nullish(),
  refundableQuantity: z.number().nullish(),
  refundedQuantity: z.number().nullish(),
  unprocessedQuantity: z.number().nullish(),
  lineItemId: z.string().nullish(),
  name: z.string().nullish(),
  sku: z.string().nullish(),
})

export const OrderReturnDeliverySchema = z.object({
  id: z.string(),
  carrierName: z.string().nullish(),
  trackingNumber: z.string().nullish(),
  trackingUrl: z.string().nullish(),
  labelUrl: z.string().nullish(),
})

export const OrderReturnSchema = z.object({
  id: z.string(),
  name: z.string().nullish(),
  status: z.string().nullish(),
  createdAt: z.string().nullish(),
  closedAt: z.string().nullish(),
  requestApprovedAt: z.string().nullish(),
  totalQuantity: z.number().nullish(),
  declineReason: z.string().nullish(),
  declineNote: z.string().nullish(),
  lineItems: z.array(OrderReturnLineItemSchema).default([]),
  reverseFulfillmentOrders: z
    .array(
      z.object({
        id: z.string(),
        status: z.string().nullish(),
        deliveries: z.array(OrderReturnDeliverySchema).default([]),
      }),
    )
    .default([]),
})

export const OrderTagSchema = z.object({
  tag: z.string(),
  description: z.string(),
})

export const OrderExceptionSchema = z.object({
  active: z.boolean(),
  stage: z.enum(["preparation", "transit"]).nullish(),
  type: z.enum(["carrier", "stock", "items", "order_data", "configuration", "internal"]).nullish(),
  message: z.string().nullish(),
  since: z.string().nullish(),
  resolvedMessage: z.string().nullish(),
})

export const OrderInformationSchema = z.object({
  referenceId: z.string(),
  orderName: z.string().nullish(),
  orderAliases: z.array(z.string()).optional(),
  externalIds: z.object({
    shopifyOrderId: z.union([z.string(), z.number()]).nullish(),
    shopifyOrderGid: z.string().nullish(),
    bigBlueOrderId: z.union([z.string(), z.number()]).nullish(),
    amphoraOrderId: z.union([z.string(), z.number()]).nullish(),
  }),
  message: z.string().optional(),
  currency: z.string().nullish(),
  shippingAddress: OrderAddressSchema,
  pudoAddress: OrderPudoAddressSchema.optional(),
  billingAddress: OrderAddressSchema,
  shippingLines: z.array(OrderShippingLineSchema).default([]),
  shipments: z.array(OrderShipmentSchema).default([]),
  returnStatus: z.string().nullish(),
  returns: z.array(OrderReturnSchema).optional(),
  products: z.array(OrderLineItemSchema).default([]),
  unmatchedLogisticsProducts: z.array(OrderLineItemSchema).optional(),
  financialStatus: z.string().nullish(),
  orderCreatedAt: z.string().nullish(),
  paidAt: z.string().nullish(),
  isInternational: z.boolean().optional(),
  isPacked: z.boolean().optional(),
  isShipped: z.boolean().optional(),
  isShopifyOrderUpdateEligible: z.boolean().optional(),
  isCancellable: z.boolean().optional(),
  isAlreadyCancelled: z.boolean().optional(),
  supportedActions: z.array(z.string()).optional(),
  metafields: z.array(OrderMetafieldSchema).optional(),
  tags: z.array(OrderTagSchema).optional(),
  // For Amphora orders only
  exception: OrderExceptionSchema.nullish(),
  isAtenea: z.boolean().optional(),
})

export type OrderInformation = z.infer<typeof OrderInformationSchema>

function normalizeLegacyPostal(value: unknown) {
  if (typeof value !== "object" || value === null || Array.isArray(value) || !("postal" in value)) {
    return value
  }

  const { postal, ...address } = value

  // Stored BigBlue results may pair its authoritative postal with Shopify's older zip.
  return { ...address, zip: postal ?? ("zip" in address ? address.zip : undefined) }
}
